import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Receipt, CreditCard, CalendarDays, Clock, Gift,
  AlertTriangle, CheckCircle, XCircle, ArrowUpCircle, Loader2, Shield, ExternalLink
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { useConfig } from "./ConfiguracoesLayout";
import ConfigSection from "./ConfigSection";

const formatCurrency = (v: number) => `R$ ${v.toFixed(2).replace(".", ",")}`;

const statusColors: Record<string, string> = {
  active: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  pending: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  cancelled: "bg-destructive/10 text-destructive border-destructive/20",
  expired: "bg-muted text-muted-foreground border-border",
};

const statusLabels: Record<string, string> = {
  active: "Ativa",
  pending: "Pendente",
  cancelled: "Cancelada",
  expired: "Expirada",
};

const Faturamento = () => {
  const navigate = useNavigate();
  const { restaurant, toast } = useConfig();
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState<any>(null);
  const [subscription, setSubscription] = useState<any>(null);
  const [allSubscriptions, setAllSubscriptions] = useState<any[]>([]);
  const [activatingTrial, setActivatingTrial] = useState(false);
  const [payingSubId, setPayingSubId] = useState<string | null>(null);
  const [confirmPaySub, setConfirmPaySub] = useState<any | null>(null);

  useEffect(() => {
    if (!restaurant) return;
    const load = async () => {
      // Fetch current plan
      if (restaurant.plan_id) {
        const { data: planData } = await supabase
          .from("plans")
          .select("*")
          .eq("id", restaurant.plan_id)
          .maybeSingle();
        setPlan(planData);
      }

      // Fetch subscriptions
      const { data: subs } = await supabase
        .from("subscriptions")
        .select("*")
        .eq("restaurant_id", restaurant.id)
        .order("created_at", { ascending: false });

      const allSubs = subs || [];
      setAllSubscriptions(allSubs);
      setSubscription(allSubs.find((s: any) => s.status === "active") || allSubs[0] || null);
      setLoading(false);
    };
    load();
  }, [restaurant]);

  const handleActivateTrial = async () => {
    if (!restaurant) return;
    setActivatingTrial(true);
    try {
      const { data: freePlan } = await supabase
        .from("plans")
        .select("id")
        .eq("is_active", true)
        .eq("price_monthly", 0)
        .maybeSingle();

      const trialEndsAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();

      await supabase
        .from("restaurants")
        .update({
          plan_id: freePlan?.id || null,
          trial_started_at: new Date().toISOString(),
          trial_ends_at: trialEndsAt,
          trial_used: true,
        })
        .eq("id", restaurant.id);

      toast({ title: "Teste gratuito ativado!", description: "Você tem 30 dias para experimentar. Após esse período, será necessário assinar um plano pago." });
      window.location.reload();
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    } finally {
      setActivatingTrial(false);
    }
  };

  const handlePayPendingSub = async (sub: any) => {
    if (!restaurant) return;
    setPayingSubId(sub.id);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: {
          plan_id: sub.plan_id,
          restaurant_id: restaurant.id,
          billing_cycle: sub.billing_cycle || "monthly",
        },
      });
      if (error) throw error;
      if (data.free) {
        toast({ title: "Plano ativado!" });
        window.location.reload();
        return;
      }
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error("Não foi possível gerar o link de pagamento");
      }
    } catch (err: any) {
      toast({ title: "Erro ao iniciar pagamento", description: err.message, variant: "destructive" });
    } finally {
      setPayingSubId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  // Compute subscription days left
  const activeSub = allSubscriptions.find((s: any) => s.status === "active");
  const periodEnd = activeSub?.current_period_end ? new Date(activeSub.current_period_end) : null;
  const daysLeft = periodEnd ? Math.max(0, Math.ceil((periodEnd.getTime() - Date.now()) / (1000 * 60 * 60 * 24))) : null;

  // Trial info
  const trialUsed = !!restaurant?.trial_used;
  const isOnTrial = !!restaurant?.trial_ends_at && new Date(restaurant.trial_ends_at) > new Date();
  const trialEndsAt = restaurant?.trial_ends_at ? new Date(restaurant.trial_ends_at) : null;
  const trialDaysLeft = trialEndsAt ? Math.max(0, Math.ceil((trialEndsAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24))) : null;
  const trialProgress = trialEndsAt && restaurant?.trial_started_at
    ? Math.min(100, ((Date.now() - new Date(restaurant.trial_started_at).getTime()) / (trialEndsAt.getTime() - new Date(restaurant.trial_started_at).getTime())) * 100)
    : 0;

  return (
    <div className="space-y-6">
      {/* Current Plan */}
      <ConfigSection title="Plano Atual" icon={CreditCard}>
        <div className="space-y-4">
          {plan ? (
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h3 className="text-lg font-bold font-display">{plan.name}</h3>
                <p className="text-sm text-muted-foreground">
                  {Number(plan.price_monthly) === 0
                    ? "Plano Gratuito"
                    : `${formatCurrency(Number(plan.price_monthly))}/mês`
                  }
                </p>
              </div>
              {activeSub && (
                <Badge className={`border ${statusColors[activeSub.status] || statusColors.expired}`}>
                  {statusLabels[activeSub.status] || activeSub.status}
                </Badge>
              )}
            </div>
          ) : (
            <div className="text-center py-4">
              <XCircle className="h-8 w-8 text-muted-foreground mx-auto mb-2 opacity-40" />
              <p className="text-sm text-muted-foreground">Nenhum plano ativo</p>
            </div>
          )}

          {/* Subscription period info */}
          {activeSub && periodEnd && (
            <div className="rounded-xl border border-border bg-secondary/30 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground flex items-center gap-2">
                  <CalendarDays className="h-4 w-4" />
                  Próxima renovação
                </span>
                <span className="text-sm font-semibold">
                  {periodEnd.toLocaleDateString("pt-BR", { day: "numeric", month: "long", year: "numeric" })}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Dias restantes
                </span>
                <span className={`text-sm font-bold ${daysLeft !== null && daysLeft <= 3 ? "text-destructive" : "text-foreground"}`}>
                  {daysLeft} {daysLeft === 1 ? "dia" : "dias"}
                </span>
              </div>
              {daysLeft !== null && daysLeft <= 7 && (
                <div className={`flex items-start gap-2 p-3 rounded-lg ${daysLeft <= 3 ? "bg-destructive/10 border border-destructive/20" : "bg-amber-500/10 border border-amber-500/20"}`}>
                  <AlertTriangle className={`h-4 w-4 mt-0.5 shrink-0 ${daysLeft <= 3 ? "text-destructive" : "text-amber-600"}`} />
                  <p className={`text-xs ${daysLeft <= 3 ? "text-destructive" : "text-amber-600"}`}>
                    {daysLeft <= 3
                      ? `Sua assinatura expira em ${daysLeft} dia${daysLeft !== 1 ? "s" : ""}! Renove para não perder acesso.`
                      : `Faltam ${daysLeft} dias para a renovação da sua assinatura.`
                    }
                  </p>
                </div>
              )}
            </div>
          )}

          <Button
            onClick={() => navigate("/dashboard?upgrade=true")}
            className="w-full gap-2"
          >
            <ArrowUpCircle className="h-4 w-4" />
            {plan ? "Alterar Plano" : "Escolher um Plano"}
          </Button>
        </div>
      </ConfigSection>

      {/* Trial Status */}
      <ConfigSection title="Teste Gratuito" icon={Gift}>
        <div className="space-y-4">
          {isOnTrial ? (
            <>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-emerald-500" />
                <span className="font-semibold text-sm">Teste gratuito ativo</span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Progresso</span>
                  <span>{trialDaysLeft} dia{trialDaysLeft !== 1 ? "s" : ""} restante{trialDaysLeft !== 1 ? "s" : ""}</span>
                </div>
                <Progress value={trialProgress} className="h-2" />
              </div>
              <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                  <div className="text-xs text-amber-700 dark:text-amber-400 space-y-1">
                    <p className="font-semibold">Atenção</p>
                    <p>
                      Durante o período de teste, os recursos são limitados ao plano gratuito.
                      Após o término, você precisará assinar um plano pago para continuar usando a plataforma.
                      <strong> O teste gratuito só pode ser usado uma vez.</strong>
                    </p>
                  </div>
                </div>
              </div>
            </>
          ) : trialUsed ? (
            <div className="flex items-start gap-3 p-4 rounded-xl border border-border bg-secondary/30">
              <Shield className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold">Teste gratuito já utilizado</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Seu período de teste gratuito já foi consumido. Para continuar usando a plataforma,
                  é necessário manter uma assinatura ativa de um plano pago.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Você ainda não utilizou seu teste gratuito. Ative agora e tenha 30 dias para experimentar a plataforma.
              </p>
              <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    Durante o trial os recursos serão limitados ao plano gratuito. Após os 30 dias,
                    será necessário assinar um plano pago. <strong>O trial só pode ser usado uma única vez.</strong>
                  </p>
                </div>
              </div>
              <Button
                onClick={handleActivateTrial}
                disabled={activatingTrial}
                variant="outline"
                className="w-full gap-2"
              >
                {activatingTrial ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gift className="h-4 w-4" />}
                Ativar Teste Gratuito de 30 dias
              </Button>
            </div>
          )}
        </div>
      </ConfigSection>

      {/* Invoice History */}
      <ConfigSection title="Histórico de Faturas" icon={Receipt}>
        {allSubscriptions.length === 0 ? (
          <div className="text-center py-6">
            <Receipt className="h-8 w-8 text-muted-foreground mx-auto mb-2 opacity-30" />
            <p className="text-sm text-muted-foreground">Nenhuma fatura encontrada</p>
          </div>
        ) : (
          <div className="space-y-2">
            {allSubscriptions.map((sub: any) => (
              <div
                key={sub.id}
                className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-secondary/30 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                    sub.status === "active" ? "bg-emerald-500/10" : sub.status === "pending" ? "bg-amber-500/10" : "bg-muted"
                  }`}>
                    {sub.status === "active" ? (
                      <CheckCircle className="h-4 w-4 text-emerald-500" />
                    ) : sub.status === "pending" ? (
                      <Clock className="h-4 w-4 text-amber-500" />
                    ) : (
                      <XCircle className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">
                      {formatCurrency(Number(sub.amount))}
                      <span className="text-muted-foreground font-normal ml-1">
                        / {sub.billing_cycle === "yearly" ? "ano" : "mês"}
                      </span>
                    </p>
                    <p className="text-[10px] text-muted-foreground">
                      {new Date(sub.created_at).toLocaleDateString("pt-BR", { day: "numeric", month: "short", year: "numeric" })}
                      {sub.current_period_start && sub.current_period_end && (
                        <> • {new Date(sub.current_period_start).toLocaleDateString("pt-BR")} — {new Date(sub.current_period_end).toLocaleDateString("pt-BR")}</>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {sub.status === "pending" && (
                    <Button
                      size="sm"
                      onClick={() => setConfirmPaySub(sub)}
                      disabled={payingSubId === sub.id}
                      className="gap-1.5 h-7 text-xs"
                    >
                      {payingSubId === sub.id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <ExternalLink className="h-3 w-3" />
                      )}
                      Pagar
                    </Button>
                  )}
                  <Badge className={`border text-[10px] ${statusColors[sub.status] || statusColors.expired}`}>
                    {statusLabels[sub.status] || sub.status}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </ConfigSection>

      {/* Confirmation Dialog for Payment */}
      <AlertDialog open={!!confirmPaySub} onOpenChange={(open) => !open && setConfirmPaySub(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
              </div>
              <AlertDialogTitle className="text-lg">Atenção: Confirmação de Pagamento</AlertDialogTitle>
            </div>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>
                  Você está prestes a ser redirecionado para a página de pagamento.
                </p>
                <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-3">
                  <p className="text-destructive font-semibold text-sm flex items-start gap-2">
                    <XCircle className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>
                      Se você <strong>não concluir o pagamento</strong>, o sistema será <strong>totalmente bloqueado</strong> e 
                      você <strong>perderá o período gratuito</strong>, não podendo mais utilizá-lo novamente.
                    </span>
                  </p>
                </div>
                <p className="text-muted-foreground">
                  Se preferir continuar usando o período de teste gratuito por enquanto, clique em <strong>"Cancelar"</strong>.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-0">
            <AlertDialogCancel>
              Cancelar e Continuar Teste
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmPaySub) {
                  handlePayPendingSub(confirmPaySub);
                }
              }}
              className="bg-primary hover:bg-primary/90"
            >
              <CreditCard className="h-4 w-4 mr-2" />
              Ir para Pagamento
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Faturamento;
